import React, { useEffect, useId, useRef, useState } from "react";
import { languages, useI18n } from "../../i18n";
import styles from "./styles/languageSelector.module.css";

export default function LanguageSelector() {
  const { locale, setLocale, t } = useI18n();
  const [open, setOpen] = useState(false);
  const root = useRef(null);
  const trigger = useRef(null);
  const menu = useRef(null);
  const id = useId();
  const current = languages.find((language) => language.code === locale);
  const options = languages.filter((language) => language.code !== locale);
  const close = () => {
    setOpen(false);
    trigger.current?.focus();
  };
  useEffect(() => {
    if (!open) return;
    menu.current?.querySelector("button")?.focus();
    const dismiss = (event) => {
      if (!root.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [open]);
  return (
    <div
      ref={root}
      className={styles.root}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) {
          event.preventDefault();
          close();
        }
      }}
    >
      <button
        ref={trigger}
        type="button"
        className={styles.trigger}
        aria-label={`${t("language")}: ${current.name}`}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={open ? id : undefined}
        onClick={() => setOpen(!open)}
        onKeyDown={(event) => {
          if (["ArrowDown", "ArrowUp"].includes(event.key)) {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span aria-hidden="true">{current.flag}</span>
        <span>{current.label}</span>
        <svg
          className={styles.chevron}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="m3 4.5 3 3 3-3"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div
          ref={menu}
          id={id}
          role="menu"
          aria-label={t("language")}
          className={styles.menu}
          onKeyDown={(event) => {
            const buttons = Array.from(menu.current.querySelectorAll("button"));
            const index = buttons.indexOf(document.activeElement);
            const positions = {
              ArrowDown: (index + 1) % buttons.length,
              ArrowUp: (index - 1 + buttons.length) % buttons.length,
              Home: 0,
              End: buttons.length - 1,
            };
            if (event.key in positions) {
              event.preventDefault();
              buttons[positions[event.key]].focus();
            }
          }}
        >
          {options.map((language) => (
            <button
              key={language.code}
              type="button"
              role="menuitem"
              lang={language.code}
              aria-label={`${language.name} (${language.label})`}
              onClick={() => {
                setLocale(language.code);
                close();
              }}
            >
              <span aria-hidden="true">{language.flag}</span>
              <span>{language.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
